import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { usStateByCode, usStateByName, usStates } from '@hero/hero-utils/address/states';
import Divider from '@hero/ui-kit/components/Divider';
import CheckboxSquare from '@hero/ui-kit/graphics/CheckboxSquare';
import Button from '@hero/ui-kit/inputs/Button';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import Filter from '../Filter';
import useOrgStorageKey from '../../hooks/useOrgStorageKey';
import LocationIcon from './LocationIcon';
import * as Style from './style.module.scss';

const LocationFilter: React.FC<{
    locations?: { code: string; name: string }[];
    searchParam?: string;
}> = ({ locations = usStates, searchParam = 'state' }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    // Persisted selection is per-organization: namespace the localStorage key by
    // the active org (the URL param name itself stays `searchParam`).
    const orgStorageKey = useOrgStorageKey();
    const storageKey = orgStorageKey(searchParam);

    React.useEffect(() => {
        const localStorageState = localStorage.getItem(storageKey);

        if (localStorageState) {
            setSearchParams((searchParams) => {
                searchParams.set(searchParam, localStorageState);
                return searchParams;
            });
        }
    }, [searchParam, storageKey, setSearchParams]);

    const updateLocationFilter = React.useCallback(
        (stateCode: string) => {
            setSearchParams((searchParams) => {
                const param = searchParams.get(searchParam)?.split(',') || [];

                const stateCodeIndex = param?.indexOf(stateCode);

                if (stateCodeIndex < 0) {
                    param.push(stateCode);
                } else {
                    param.splice(stateCodeIndex, 1);
                }

                const stateName = usStateByCode(stateCode)?.name;

                if (stateName) {
                    const stateNameIndex = param?.indexOf(stateName);

                    if (stateNameIndex < 0) {
                        param.push(stateName);
                    } else {
                        param.splice(stateNameIndex, 1);
                    }
                }

                const paramString = param.filter((i) => !!i).join(',');

                if (paramString) {
                    searchParams.set(searchParam, paramString);
                    localStorage.setItem(storageKey, paramString);
                } else {
                    searchParams.delete(searchParam);
                    localStorage.removeItem(storageKey);
                }

                searchParams.delete('offset');

                return searchParams;
            });
        },
        [setSearchParams, searchParam, storageKey]
    );

    const handleClear = React.useCallback(() => {
        setSearchParams((prev) => {
            prev.delete(searchParam);
            localStorage.removeItem(storageKey);

            return prev;
        });
    }, [searchParam, storageKey, setSearchParams]);

    const filterLabel = React.useMemo(() => {
        const locations = searchParams.get(searchParam);

        if (locations) {
            const locationArray = locations.split(',').map((location) => {
                if (location.length > 2) {
                    const stateCode = usStateByName(location)?.code;

                    return stateCode ? stateCode : location;
                }

                return location;
            });

            const uniqueArray = [...new Set(locationArray)];

            return uniqueArray.map((item) => usStateByCode(item)?.name).join(', ');
        }

        return 'Location: All';
    }, [searchParam, searchParams]);

    const selectedLocations = searchParams.get(searchParam);
    const [searchTerm, setSearchTerm] = React.useState('');

    const filteredLocations = React.useMemo(() => {
        if (!searchTerm) return locations;
        const term = searchTerm.toLowerCase();
        return locations.filter((l) => l.name.toLowerCase().includes(term));
    }, [locations, searchTerm]);

    return (
        <Filter
            filterLabel={
                <button
                    className={`${Style.filterButton} ${
                        typeof selectedLocations === 'string' ? Style.activeFilter : ''
                    }`}
                >
                    <LocationIcon type="utility" size="utility" />
                    <div>{filterLabel}</div>
                </button>
            }
            width="34rem"
            className={Style.filterBtnLabel}
            dropdownPosition="right"
        >
            {locations.length > 5 ? (
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className={Style.searchInput}
                    autoFocus
                />
            ) : null}
            <div className={Style.filterWrapper}>
                {filteredLocations.map((location) => {
                    return (
                        <ClickableOpacity
                            fullWidth
                            key={location.code}
                            alt={`Toggle ${location.name}`}
                            className={Style.filterStatusItem}
                            onClick={() => {
                                updateLocationFilter(location.code);
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <CheckboxSquare
                                    isChecked={
                                        searchParams.get(searchParam)?.includes(location.code) ||
                                        false
                                    }
                                    isFocused={false}
                                    isHovered={false}
                                    disabled={false}
                                    styles={{
                                        minWidth: '1.6rem',
                                        height: '1.6rem',
                                        width: '1.6rem'
                                    }}
                                />

                                <P noDefaultMargin>{location.name}</P>
                            </div>
                        </ClickableOpacity>
                    );
                })}
            </div>
            {selectedLocations ? (
                <>
                    <Divider styles={{ margin: '0 calc(var(--gutter) / 2)' }} />
                    <Section centered noDefaultPadding>
                        <Button
                            variant="primary"
                            noDefaultMargin
                            className={Style.resetBtn}
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    </Section>
                </>
            ) : null}
        </Filter>
    );
};

export default LocationFilter;
